import React, { useCallback, useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  Avatar,
  Box,
  Button,
  DataTable,
  DataTableCellVariants,
  DropdownMenu,
  DropdownMenuItem,
  Highlight,
  Icon,
  Pagination,
  SidePanel,
  toast,
  useSidePanel,
  type DataTableHeaderProps,
  type DataTableRowData,
  type RowsPerPageType,
} from '@node-space/ui-components'
import { useProfileContext } from 'hooks/context/useProfileContext'
import { useDeletePaymentLimitMutation } from 'hooks/mutations/useDeletePaymentLimitMutation'
import { useMappedRoles } from 'hooks/useMappedRoles'
import { PaymentLimitSheet } from 'pages/PaymentLimits/components/PaymentLimitSheet'
import {
  canEditLimit,
  formatOwnerLabel,
  formatPaymentMethodLabel,
  getOwnerHighlightType,
  LIMIT_TYPE_LABELS,
} from 'pages/PaymentLimits/utils'
import { reactQueryName } from 'reactQueryKeys/reactQueryKeys'
import {
  PaymentLimitAction,
  PaymentLimitSheetType,
  type Limit,
  type PaymentLimitActionType,
} from 'types/paymentLimits'
import { highlightRowBasedOnId } from 'utils/table/table'
import { roundNumberWithCommas } from 'utils/utils'
import {
  PaymentLimitSidePanelBody,
  PaymentLimitSidePanelHeader,
} from './PaymentLimitSidePanelContent'

interface PaymentLimitsTableProps {
  limits: Limit[]
  tableName: string
  customerId?: string
  page: number
  rowsPerPage: RowsPerPageType
  totalElements: number
  handleChangePage: (page: number) => void
  handleChangeRowsPerPage: (rowsPerPage: RowsPerPageType) => void
}

export const PaymentLimitsTable = ({
  limits,
  tableName,
  customerId,
  page,
  rowsPerPage,
  totalElements,
  handleChangePage,
  handleChangeRowsPerPage,
}: PaymentLimitsTableProps) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { profile } = useProfileContext()
  const permissions = useMappedRoles()
  const canEditPaymentLimits = permissions?.paymentLimits?.canEdit ?? false

  const [isAddSheetOpen, setIsAddSheetOpen] = useState(false)
  const [isEditSheetOpen, setIsEditSheetOpen] = useState(false)
  const [selectedLimit, setSelectedLimit] = useState<Limit | null>(null)

  const { mutate: deleteLimit, isPending: isDeleting } = useDeletePaymentLimitMutation()

  const {
    isSidePanelOpen,
    sidePanelData: selectedSidePanelLimit,
    paging: sidePanelPaging,
    setIsSidePanelOpen,
    updateSidePanel,
  } = useSidePanel(limits)

  const handleCloseEditSheet = useCallback(() => {
    setIsEditSheetOpen(false)
    setSelectedLimit(null)
  }, [])

  const handleAction = useCallback(
    (action: PaymentLimitActionType, limit: Limit, index?: number) => {
      switch (action) {
        case PaymentLimitAction.VIEW: {
          if (index !== undefined) {
            updateSidePanel(index)
            setIsSidePanelOpen(true)
          }
          break
        }
        case PaymentLimitAction.EDIT:
          setSelectedLimit(limit)
          setIsEditSheetOpen(true)
          break
        case PaymentLimitAction.DELETE:
          deleteLimit(limit.id, {
            onSuccess: () => {
              toast({ header: t('paymentLimits.limitDeletedSuccess'), variant: 'success' })
              queryClient.invalidateQueries({
                queryKey: [reactQueryName.PAYMENT_LIMITS],
              })
            },
            onError: () => {
              toast({ header: t('paymentLimits.limitDeleteError'), variant: 'error' })
            },
          })
          break
      }
    },
    [updateSidePanel, setIsSidePanelOpen, deleteLimit, t, queryClient]
  )

  const columns: DataTableHeaderProps[] = useMemo(
    () => [
      {
        key: 'paymentMethod',
        label: t('customers.paymentLimitsTable.paymentMethod'),
        variant: DataTableCellVariants.TEXT,
      },
      {
        key: 'limitType',
        label: t('customers.paymentLimitsTable.limitType'),
        variant: DataTableCellVariants.HIGHLIGHT,
      },
      {
        key: 'amount',
        label: t('customers.paymentLimitsTable.amount'),
        variant: DataTableCellVariants.TEXT,
        className: 'text-right',
      },
      {
        key: 'currency',
        label: t('customers.paymentLimitsTable.currency'),
        variant: DataTableCellVariants.TEXT,
      },
      {
        key: 'owner',
        label: t('customers.paymentLimitsTable.owner'),
        variant: DataTableCellVariants.HIGHLIGHT,
      },
      {
        key: 'actions',
        label: '',
        variant: DataTableCellVariants.ACTION,
      },
    ],
    [t]
  )

  const tableData: DataTableRowData[] = useMemo(() => {
    if (!limits?.length) return []

    return limits.map((limit, index) => ({
      id: limit?.id ?? `limit-${index}`,
      testid: limit?.id,
      onRowClick: () => handleAction(PaymentLimitAction.VIEW, limit, index),
      cells: [
        {
          key: 'paymentMethod',
          variant: DataTableCellVariants.TEXT,
          Text: formatPaymentMethodLabel(limit.method ?? limit.qualifiers?.method ?? ''),
        },
        {
          key: 'limitType',
          variant: DataTableCellVariants.HIGHLIGHT,
          Highlights: [
            <Highlight
              key={`${limit.id}-limitType`}
              type={limit.limitType === 'DAILY' ? 'feature' : 'neutral'}
              variant="emphasis"
            >
              {t(LIMIT_TYPE_LABELS[limit.limitType] ?? limit.limitType)}
            </Highlight>,
          ],
        },
        {
          key: 'amount',
          variant: DataTableCellVariants.TEXT,
          Text: roundNumberWithCommas(limit.amount, 2),
          className: 'text-right',
        },
        {
          key: 'currency',
          variant: DataTableCellVariants.TEXT,
          Text: (
            <Box flex align="center" gap={8}>
              <Avatar name={limit.currency} type="currency" size="small" />
              <Box className="text-body-regular text-foreground-prominent">{limit.currency}</Box>
            </Box>
          ),
        },
        {
          key: 'owner',
          variant: DataTableCellVariants.HIGHLIGHT,
          Highlights: limit.owner
            ? [
                <Highlight
                  key={`${limit.id}-owner`}
                  type={getOwnerHighlightType(limit.owner)}
                  variant="emphasis"
                >
                  {formatOwnerLabel(limit.owner)}
                </Highlight>,
              ]
            : [],
        },
        {
          key: 'actions',
          variant: DataTableCellVariants.ACTION,
          Actions: [
            <Box key={`menu-${limit.id}`} testId={`limit-menu-${limit.id}`}>
              <DropdownMenu
                DropdownMenuItems={[
                  <DropdownMenuItem
                    key="view"
                    LeadingIcon={<Icon name="Eye" />}
                    onClick={() => handleAction(PaymentLimitAction.VIEW, limit, index)}
                  >
                    {t('paymentLimits.viewDetails')}
                  </DropdownMenuItem>,
                  ...(canEditLimit(limit, canEditPaymentLimits, profile?.roles)
                    ? [
                        <DropdownMenuItem
                          key="edit"
                          LeadingIcon={<Icon name="SimplePencilCustom" />}
                          onClick={() => handleAction(PaymentLimitAction.EDIT, limit)}
                        >
                          {t('paymentLimits.editLimit')}
                        </DropdownMenuItem>,
                        <DropdownMenuItem
                          key="delete"
                          LeadingIcon={<Icon name="Trash" />}
                          disabled={isDeleting}
                          onClick={() => handleAction(PaymentLimitAction.DELETE, limit)}
                        >
                          {t('paymentLimits.removeLimit')}
                        </DropdownMenuItem>,
                      ]
                    : []),
                ]}
              >
                <Button
                  variant="ghost"
                  noPadding
                  iconElement={<Icon name="DotsThreeVerticalCustom" />}
                />
              </DropdownMenu>
            </Box>,
          ],
        },
      ],
    }))
  }, [limits, t, handleAction, isDeleting, canEditPaymentLimits, profile?.roles])

  return (
    <>
      <DataTable name={tableName} headers={columns} data={tableData} />

      {totalElements > rowsPerPage && (
        <Box paddingT={16}>
          <Pagination
            testId={`${tableName}-pagination`}
            page={page}
            rowsPerPage={rowsPerPage}
            total={totalElements}
            onChangePage={handleChangePage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
          />
        </Box>
      )}

      <PaymentLimitSheet
        isOpen={isAddSheetOpen}
        type={PaymentLimitSheetType.ADD}
        customerId={customerId}
        onClose={() => setIsAddSheetOpen(false)}
      />

      <PaymentLimitSheet
        isOpen={isEditSheetOpen}
        type={PaymentLimitSheetType.EDIT}
        limit={selectedLimit}
        customerId={customerId}
        onClose={handleCloseEditSheet}
      />

      <SidePanel
        id={customerId ? 'customer-payment-limit-side-panel' : 'payment-limit-side-panel'}
        isOpen={isSidePanelOpen}
        onClose={() => setIsSidePanelOpen(false)}
        paging={sidePanelPaging}
        activeRowId={selectedSidePanelLimit?.id}
        onActiveRowChange={highlightRowBasedOnId}
        header={<PaymentLimitSidePanelHeader limit={selectedSidePanelLimit ?? null} />}
        body={
          <PaymentLimitSidePanelBody
            limit={selectedSidePanelLimit ?? null}
            titleKeys={{
              currency: 'paymentLimits.currency',
              paymentMethod: 'paymentLimits.paymentMethod',
              limitType: 'paymentLimits.limitTypeLabel',
            }}
          />
        }
      />
    </>
  )
}
